type DisposeFn = () => void;

type DisposableEntry = {
  name: string;
  dispose: DisposeFn;
};

export class SessionTeardownService {
  // RTDB delivers listener-revocation callbacks slightly after the auth
  // session ends, so the "in progress" answer must stay true for a beat
  // after a teardown even if a new session starts immediately.
  private static readonly POST_TEARDOWN_GRACE_MS = 2000;

  private static disposers = new Map<number, DisposableEntry>();
  private static nextId = 1;
  private static isTearingDown = false;
  private static lastTeardownStartedAt: number | null = null;

  public static registerDisposable(
    name: string,
    dispose: DisposeFn,
  ): () => void {
    const id = SessionTeardownService.nextId;
    SessionTeardownService.nextId += 1;
    SessionTeardownService.disposers.set(id, { name, dispose });

    return () => {
      SessionTeardownService.disposers.delete(id);
    };
  }

  public static teardownAll(): void {
    SessionTeardownService.isTearingDown = true;
    SessionTeardownService.lastTeardownStartedAt = Date.now();
    const entries = Array.from(SessionTeardownService.disposers.values());
    SessionTeardownService.disposers.clear();

    entries.forEach((entry) => {
      try {
        entry.dispose();
      } catch (error) {
        console.error(`Failed to dispose ${entry.name}`, error);
      }
    });
  }

  public static reset(): void {
    SessionTeardownService.isTearingDown = false;
  }

  public static isTeardownInProgress(): boolean {
    if (SessionTeardownService.isTearingDown) {
      return true;
    }
    return (
      SessionTeardownService.lastTeardownStartedAt !== null &&
      Date.now() - SessionTeardownService.lastTeardownStartedAt <
        SessionTeardownService.POST_TEARDOWN_GRACE_MS
    );
  }
}
